import { useEffect, useRef, useState } from 'react';
import { VisuallyHidden } from '@vp/swan';
import { __ } from '@99designs/i18n';
import type { ChatSendState } from '../../hooks/useChat';

export type ChatStatusRegionProps = {
    loading: boolean;
    sendState: ChatSendState;
};

/**
 * The single live region for the chat, and the only thing that tells a screen reader
 * what the conversation is doing.
 *
 * Deliberately renders unconditionally, even with nothing to say. SWAN is explicit that
 * a live region must not be conditionally rendered: assistive technology has to observe
 * the element before its content changes, so mounting it at the same moment as the
 * message means the announcement is missed. The *text* is what changes, never the
 * element.
 *
 * It owns the copy rather than receiving it, so callers pass chat state and this is the
 * one place that decides how the conversation sounds. `"Response ready"` needs the
 * previous send state because it describes a transition rather than a state — hence the
 * ref, which lives here with the decision it serves instead of leaking into the caller.
 *
 * Errors are deliberately absent: both banners render a SWAN AlertBox with
 * `role="alert"`, which announces itself on insertion. Repeating them here would speak
 * the same failure twice.
 */
export function ChatStatusRegion({ loading, sendState }: ChatStatusRegionProps) {
    const [statusText, setStatusText] = useState('');
    const previousSendState = useRef(sendState);

    useEffect(() => {
        const previous = previousSendState.current;
        previousSendState.current = sendState;

        if (loading) {
            setStatusText(__('Loading chat'));
        } else if (sendState === 'sending') {
            setStatusText(__('Generating a response'));
        } else if (previous === 'sending' && sendState === 'idle') {
            // Only meaningful as a transition — a reply just landed.
            setStatusText(__('Response ready'));
        } else {
            setStatusText('');
        }
    }, [loading, sendState]);

    return (
        <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
            {statusText}
        </VisuallyHidden>
    );
}
